import clsx from 'clsx';
import { useCallback, useLayoutEffect, useMemo } from 'react';
import { matchPath, NavLink, Outlet, useLocation } from 'react-router-dom';
import { LoadingTracker } from 'src/components/common/LoadingTracker';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { usePageLoadStatus } from 'src/hooks/usePageLoadStatus';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasAutopricingFeature } from 'src/hooks/useUserHasAutopricingFeature';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { UnauthorizedAccessMessageContainer } from 'src/navigations/LayoutContent/LayoutContent.styled';
import { ContentId } from 'src/utils/constants/contentId';
import { SETTINGS_VIEWS_TO_CONTENT_ID_MAP } from 'src/utils/constants/contentIdMaps';
import { Feature, Permission } from 'src/WebApiController';

import { MainRoute } from '../MainRoute';
import * as reportsStyles from '../Reports/Reports.css';
import { SettingsTree, SettingTab } from './constants';
import * as styles from './Settings.css';

export const Settings = () => {
  const { appContext } = useAppContext();
  const { registerComponent } = usePageLoadStatus();

  const settingsText = useContent(ContentId.Settings);
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    registerComponent('SettingsContent');
  }, [registerComponent]);

  const hasEventDiscoveryFeature = useUserHasFeature(
    Feature.EventDiscoveryPage
  );

  const hasSiteVersionFeature = useUserHasFeature(Feature.DisplayBuildVersion);
  const hasTeamManagementFeature = useUserHasFeature(Feature.TeamManagement);
  const hasCurrencyConversionSettingsTabFeature = useUserHasFeature(
    Feature.CurrencyConversionSettingsTab
  );
  const hasAdminPermission = useUserHasAnyOfPermissions(
    Permission.SellerAccount_Admin
  );
  const hasAccountViewAutoPricingSettingsPermission =
    useUserHasAnyOfPermissions(Permission.Accounting_ViewAutoPricingSettings);
  const hasFulfillmentProvidersFeature = useUserHasFeature(
    Feature.FulfillmentProviders
  );

  const hasAutoPricingTabFeature = useUserHasAutopricingFeature(
    Feature.AutoPricingTabInAccountSettings
  );

  const hasAutoSplitDiscontinuousListingSettingFeature = useUserHasFeature(
    Feature.AutoSplitDiscontinuousListingSetting
  );

  const hasVendorManagementPermission = useUserHasAnyOfPermissions(
    Permission.Purchases_Edit,
    Permission.Purchases_EditOwned,
    Permission.Purchases_EditVendor
  );

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const hasConsignorMasterUI_UserManagementFeature = useUserHasFeature(
    Feature.UserManagementMasterUiForConsignor
  );

  const hasSellerEventAlertMutesFeature = useUserHasFeature(
    Feature.SellerEventAlertMutes
  );

  const isAuthorized = useMemo(
    () => ({
      [SettingTab.UserManagement]:
        hasAdminPermission &&
        (!hasConsignorMasterUI || hasConsignorMasterUI_UserManagementFeature),
      [SettingTab.UserManagement_Users]:
        hasAdminPermission &&
        (!hasConsignorMasterUI || hasConsignorMasterUI_UserManagementFeature),
      [SettingTab.UserManagement_Roles]:
        hasAdminPermission &&
        (!hasConsignorMasterUI || hasConsignorMasterUI_UserManagementFeature),
      [SettingTab.UserManagement_Teams]:
        hasAdminPermission && hasTeamManagementFeature && !hasConsignorMasterUI,
      [SettingTab.SellerAccount]: false,
      [SettingTab.VendorManagement]:
        hasVendorManagementPermission && !hasConsignorMasterUI,
      [SettingTab.VendorManagement_Payments]:
        hasVendorManagementPermission && !hasConsignorMasterUI,
      [SettingTab.VendorManagement_Vendors]:
        hasVendorManagementPermission && !hasConsignorMasterUI,
      [SettingTab.MyAccount]: true,
      [SettingTab.SyncCenter]: hasAdminPermission && !hasConsignorMasterUI,
      [SettingTab.CurrencyConversion]:
        hasAdminPermission &&
        hasCurrencyConversionSettingsTabFeature &&
        !hasConsignorMasterUI,
      [SettingTab.PurchaseOrder]: hasAdminPermission && !hasConsignorMasterUI,
      [SettingTab.FulfillmentProviders]:
        hasAdminPermission &&
        hasFulfillmentProvidersFeature &&
        !hasConsignorMasterUI,
      [SettingTab.AutoPricing]:
        hasAccountViewAutoPricingSettingsPermission &&
        hasAutoPricingTabFeature &&
        !hasConsignorMasterUI,
      [SettingTab.DialogSuppression]:
        hasAdminPermission && hasSellerEventAlertMutesFeature,
      [SettingTab.EventDiscovery]:
        hasAdminPermission && hasEventDiscoveryFeature && !hasConsignorMasterUI,
      [SettingTab.Accounting]: hasAdminPermission && !hasConsignorMasterUI,
      [SettingTab.Inventory]: hasAdminPermission && !hasConsignorMasterUI,
    }),
    [
      hasAdminPermission,
      hasConsignorMasterUI,
      hasConsignorMasterUI_UserManagementFeature,
      hasTeamManagementFeature,
      hasVendorManagementPermission,
      hasCurrencyConversionSettingsTabFeature,
      hasFulfillmentProvidersFeature,
      hasAccountViewAutoPricingSettingsPermission,
      hasAutoPricingTabFeature,
      hasSellerEventAlertMutesFeature,
      hasEventDiscoveryFeature,
    ]
  );

  const isPathActive = useCallback(
    (id: SettingTab) =>
      matchPath(pathname, `/settings/${SettingsTree[id]}${id}`),
    [pathname]
  );

  const hasPermissionsForActiveView = useMemo(() => {
    const activePath = Object.keys(isAuthorized).find((key) =>
      isPathActive(key as SettingTab)
    );

    const isRootPathActive = matchPath(pathname, '/settings');

    const hasPermissionsForActiveView =
      isRootPathActive || (isAuthorized[activePath as SettingTab] ?? false);

    return hasPermissionsForActiveView;
  }, [isAuthorized, pathname, isPathActive]);

  return (
    <LayoutContent
      mainRoute={MainRoute.Settings}
      routeTitle={settingsText}
      rightContent={
        hasSiteVersionFeature && appContext ? (
          <div className={styles.versionNumber}>
            <Content id={ContentId.AppVersion} />: <b>{appContext.version}</b>
          </div>
        ) : null
      }
    >
      <div className={reportsStyles.root}>
        <div className={styles.mainContent}>
          <div
            className={clsx(styles.tabsContainer, styles.tabsContainerWidth)}
          >
            {(
              Object.entries(SETTINGS_VIEWS_TO_CONTENT_ID_MAP) as [
                SettingTab,
                ContentId,
              ][]
            ).map(([id, contentId]) => {
              const parent = SettingsTree[id as string];
              return isAuthorized[id] ? (
                <NavLink
                  key={id}
                  to={`/settings/${parent}${id}`}
                  className={({ isActive }) => {
                    return clsx(
                      reportsStyles.navTitle,
                      reportsStyles.textPrimary,
                      {
                        [reportsStyles.navTitleActive]: isActive,
                        [reportsStyles.navTitleSecondary]: parent !== '',
                      }
                    );
                  }}
                >
                  <Content id={contentId} />
                </NavLink>
              ) : null;
            })}
          </div>
          <div
            className={clsx(styles.viewContainer, {
              [styles.myAccount]: isPathActive(SettingTab.MyAccount),
              [styles.eventDiscovery]: isPathActive(SettingTab.EventDiscovery),
              [styles.dialogSuppression]: isPathActive(
                SettingTab.DialogSuppression
              ),
            })}
          >
            {hasPermissionsForActiveView ? (
              <LoadingTracker componentName="SettingsContent">
                <Outlet />
              </LoadingTracker>
            ) : (
              <UnauthorizedAccessMessageContainer>
                <Content id={ContentId.AccessToViewUnauthorized} />
              </UnauthorizedAccessMessageContainer>
            )}
          </div>
        </div>
      </div>
    </LayoutContent>
  );
};
